import React from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';

const LegalPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Simple Back Button */}
      <div className="container mx-auto px-4 py-6">
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>
      </div>

      <div className="flex-grow px-4 pb-8">
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Legal & Privacy</h1>
          
          <div className="space-y-8 text-gray-700">
            {/* Privacy Section */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4 flex items-center gap-2">
                <span>🔒</span> Privacy
              </h2>
              <div className="space-y-3">
                <p>
                  <strong>What we track:</strong> We use Google Analytics to understand general usage patterns 
                  (page visits, feature usage, anonymous demographics).
                </p>
                <p>
                  <strong>What stays private:</strong> Your tinnitus frequency and all audio processing 
                  happen entirely in your browser. We never see or store this data.
                </p>
                <p>
                  <strong>What we store locally:</strong> Your progress is saved in your browser's localStorage. 
                  You can clear this anytime through your browser settings.
                </p>
                <p>
                  <strong>No account needed:</strong> We don't require registration or any personal information.
                </p>
              </div>
            </section>

            {/* Terms Section */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4 flex items-center gap-2">
                <span>📋</span> Terms of Use
              </h2>
              <div className="space-y-3">
                <p>
                  <strong>What this is:</strong> A free self-help tool for identifying tinnitus frequencies 
                  and providing notched sound therapy based on research.
                </p>
                <p>
                  <strong>Medical disclaimer:</strong> Not medical advice, diagnosis, or treatment. 
                  Not FDA evaluated. Always consult healthcare professionals for tinnitus concerns.
                </p>
                <p>
                  <strong>Use safely:</strong> Keep volumes at comfortable levels. Take breaks. 
                  Stop if you experience discomfort.
                </p>
                <p>
                  <strong>No warranty:</strong> Provided "as is" for informational purposes. 
                  Results vary by individual. We're not liable for outcomes.
                </p>
              </div>
            </section>

            {/* Cookies Section */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4 flex items-center gap-2">
                <span>🍪</span> Cookies
              </h2>
              <div className="space-y-2">
                <p>We use cookies for:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Saving your test progress locally</li>
                  <li>Google Analytics (anonymous usage data)</li>
                  <li>Remembering your consent preferences</li>
                </ul>
                <p className="mt-3">
                  You can disable cookies in your browser settings, though some features may not work properly.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4 flex items-center gap-2">
                <span>©️</span> Intellectual Property
              </h2>
              <p>
                TinnitusTune and its content are owned by Soepel IT and protected by copyright law. 
                The tool is free to use but not to copy or redistribute.
              </p>
            </section>

            {/* Jurisdiction */}
            <section className="pb-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-purple-700 mb-4 flex items-center gap-2">
                <span>⚖️</span> Legal Jurisdiction
              </h2>
              <p>
                These terms are governed by the laws of the Netherlands. 
                Any disputes shall be resolved in the courts of Amsterdam, the Netherlands. 
                EU consumer protection laws apply where applicable.
              </p>
            </section>

            {/* Contact Section */}
            <section>
              <h2 className="text-xl font-semibold text-purple-700 mb-4 flex items-center gap-2">
                <span>📧</span> Contact
              </h2>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="font-semibold mb-2">Soepel IT</p>
                <p>Enschede, Netherlands</p>
                <p>
                  Email:{' '}
                  <a href="mailto:tinnitustune@soepel.it" className="text-purple-600 hover:text-purple-700 underline">
                    tinnitustune@soepel.it
                  </a>
                </p>
                <p>
                  Website:{' '}
                  <a href="https://soepel.it" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                    soepel.it
                  </a>
                </p>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 mb-6">
              Last updated: {new Date().toLocaleDateString()}
            </p>
            <Link 
              to="/" 
              className="inline-block bg-gradient-to-r from-purple-600 to-pink-500 text-white px-6 py-3 rounded-full hover:shadow-lg transition-all duration-200 transform hover:scale-105 font-semibold"
            >
              I Understand - Back to App
            </Link>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default LegalPage;